/**
 * ChunkedVoxelGrid: the persistent, structurally shared document base (docs/large-volume-plan.md §1, §2.1).
 *
 * A two-level trie of 16³ chunks (64 top entries → leaves of 512 chunk refs → Uint16 palette ids).
 * It satisfies the `VoxelMap` read/write surface (`get`, `has`, `set`, `delete`, `clear`, `size`,
 * `forEach`, iteration) plus Immutable-style transients (`asMutable` / `asImmutable`) and span writes.
 *
 * Copy-on-change: a transient carries an owner token; a chunk (or leaf) is copied the first time the
 * transient changes a cell in it and then written in place for the rest of that transient. Writing a
 * cell to the palette id it already holds copies nothing. Frozen grids (owner 0) are read-only; writes
 * throw, mirroring that Immutable writes on a persistent map never affect the receiver.
 *
 * Bounds: the grid clips to a bounds box (defaulting to the whole 512³ Morton domain). Keys outside
 * the box, negative, non-integer or beyond 27 bits are ignored: `set` is a no-op returning `this`,
 * `delete` returns false, `get` returns undefined. Writers in `layerSystem.ts` already clip to the
 * document bounds, so for in-domain documents this matches today's behaviour; out-of-bounds keys were
 * garbage under Immutable (Morton encoding wraps negative coordinates) and are dropped here instead.
 *
 * This module imports only `voxelTypes` and `voxelMath` (ADR 0001 layering): no React, registry,
 * renderers, atoms or events.
 */
import { parseKeyX, parseKeyY, parseKeyZ, type Position } from '@/voxelMath'
import type { VoxelData } from '@/voxelTypes'
import {
  CHUNK_BYTES,
  CHUNK_MASK,
  CHUNK_SHIFT,
  KEY_LIMIT,
  LINEAR_TO_MORTON12,
  MORTON12_TO_LINEAR,
  TOP_ENTRIES,
  chunkClipTable,
  chunkIdOfChunkXYZ,
  normalizeBounds,
  type ChunkClipTable,
} from './layout'
import { copyChunk, copyLeaf, createChunk, createLeaf, type ChunkLeaf, type VoxelChunk } from './chunk'
import { VoxelPalette } from './palette'

export type SpanOp =
  | { kind: 'place' | 'fill' | 'repaint'; id: number }
  | { kind: 'subtract' }
  | { kind: 'accrete'; id: number; canOverwrite: Uint8Array /* indexed by palette id */ }
  /** Scoped dig (B5): clears only occupied cells whose palette id is flagged in `canWrite`. */
  | { kind: 'subtractIf'; canWrite: Uint8Array /* indexed by palette id; 1 = in scope */ }
  /** Smear (B5): rewrites to `id` only occupied cells whose palette id is flagged in `canWrite`. */
  | { kind: 'repaintIf'; id: number; canWrite: Uint8Array /* indexed by palette id; 1 = in scope */ }

let nextOwnerToken = 1
const GRID_BRAND: unique symbol = Symbol.for('huebo.ChunkedVoxelGrid') as never

// Module-local aliases of the hot constants. Imported bindings compile to live getter accesses under
// Vite's SSR transform (vitest / vite-node), which roughly doubled per-cell get/set cost in benches;
// locals keep the per-cell paths flat there and cost nothing in a bundled build.
const M2L = MORTON12_TO_LINEAR
const L2M = LINEAR_TO_MORTON12
const KEY_END = KEY_LIMIT

type InternalInit = { top: (ChunkLeaf | null)[]; size: number; owner: number }

export class ChunkedVoxelGrid {
  readonly [GRID_BRAND] = true as const
  readonly palette: VoxelPalette
  readonly bounds: Readonly<Position>
  private top: (ChunkLeaf | null)[]
  private _size: number
  /** 0 = frozen; otherwise the transient token that may write nodes it owns in place. */
  private owner: number
  private _freshChunkBytes = 0
  private readonly clip: ChunkClipTable | null

  constructor(palette: VoxelPalette = new VoxelPalette(), bounds?: Readonly<Position> | null, internal?: InternalInit) {
    this.palette = palette
    this.bounds = normalizeBounds(bounds)
    this.clip = chunkClipTable(this.bounds)
    if (internal) {
      this.top = internal.top
      this._size = internal.size
      this.owner = internal.owner
    } else {
      this.top = new Array<ChunkLeaf | null>(TOP_ENTRIES).fill(null)
      this._size = 0
      this.owner = 0
    }
  }

  // --- Map surface -------------------------------------------------------------------------

  get size(): number {
    return this._size
  }

  get(key: number): VoxelData | undefined {
    const leaf = this.top[key >>> 21]
    if (!leaf) return undefined
    const chunk = leaf.chunks[(key >>> 12) & 511]
    if (chunk === null) return undefined
    const id = chunk.data[M2L[key & 4095]]
    return id === 0 ? undefined : this.palette.items[id]
  }

  has(key: number): boolean {
    return this.idAt(key) !== 0
  }

  /** Palette id at `key` (0 = empty or outside the domain). */
  idAt(key: number): number {
    const leaf = this.top[key >>> 21]
    if (!leaf) return 0
    const chunk = leaf.chunks[(key >>> 12) & 511]
    if (chunk === null) return 0
    return chunk.data[M2L[key & 4095]]
  }

  /** Visits every non-empty cell in chunk-Morton order (linear x-fastest order inside a chunk). */
  forEach(fn: (value: VoxelData, key: number, grid: this) => void): void {
    const items = this.palette.items
    const top = this.top
    for (let t = 0; t < TOP_ENTRIES; t++) {
      const leaf = top[t]
      if (!leaf) continue
      const chunks = leaf.chunks
      for (let s = 0; s < 512; s++) {
        const chunk = chunks[s]
        if (chunk === null || chunk.count === 0) continue
        const keyBase = ((t << 9) | s) << 12
        const d = chunk.data
        for (let i = 0; i < 4096; i++) {
          const id = d[i]
          if (id !== 0) fn(items[id] as VoxelData, keyBase | L2M[i], this)
        }
      }
    }
  }

  *keys(): IterableIterator<number> {
    const top = this.top
    for (let t = 0; t < TOP_ENTRIES; t++) {
      const leaf = top[t]
      if (!leaf) continue
      for (let s = 0; s < 512; s++) {
        const chunk = leaf.chunks[s]
        if (chunk === null || chunk.count === 0) continue
        const keyBase = ((t << 9) | s) << 12
        const d = chunk.data
        for (let i = 0; i < 4096; i++) if (d[i] !== 0) yield keyBase | L2M[i]
      }
    }
  }

  *values(): IterableIterator<VoxelData> {
    const items = this.palette.items
    for (let t = 0; t < TOP_ENTRIES; t++) {
      const leaf = this.top[t]
      if (!leaf) continue
      for (let s = 0; s < 512; s++) {
        const chunk = leaf.chunks[s]
        if (chunk === null || chunk.count === 0) continue
        const d = chunk.data
        for (let i = 0; i < 4096; i++) if (d[i] !== 0) yield items[d[i]] as VoxelData
      }
    }
  }

  *entries(): IterableIterator<[number, VoxelData]> {
    const items = this.palette.items
    for (let t = 0; t < TOP_ENTRIES; t++) {
      const leaf = this.top[t]
      if (!leaf) continue
      for (let s = 0; s < 512; s++) {
        const chunk = leaf.chunks[s]
        if (chunk === null || chunk.count === 0) continue
        const keyBase = ((t << 9) | s) << 12
        const d = chunk.data
        for (let i = 0; i < 4096; i++) {
          const id = d[i]
          if (id !== 0) yield [keyBase | L2M[i], items[id] as VoxelData]
        }
      }
    }
  }

  [Symbol.iterator](): IterableIterator<[number, VoxelData]> {
    return this.entries()
  }

  // --- Transients ---------------------------------------------------------------------------

  /** True while this grid is a transient that may be written in place. */
  get isMutable(): boolean {
    return this.owner !== 0
  }

  /**
   * Begins a transient: an O(1) copy of the 64 top refs with a fresh owner token. Chunks and leaves
   * are copied lazily, the first time the transient changes one. On a transient this returns `this`.
   */
  asMutable(): ChunkedVoxelGrid {
    if (this.owner !== 0) return this
    return new ChunkedVoxelGrid(this.palette, this.bounds, { top: this.top.slice(), size: this._size, owner: ++nextOwnerToken })
  }

  /** Freezes this grid in place (owner 0) and returns it. Further writes throw. */
  asImmutable(): ChunkedVoxelGrid {
    this.owner = 0
    return this
  }

  /** Bytes of chunk storage first created (allocated or copied) by this version's transient. */
  get freshChunkBytes(): number {
    return this._freshChunkBytes
  }

  // --- Writes -------------------------------------------------------------------------------

  /** Sets `key` to `value` (interned into the palette). No chunk is copied if the id is unchanged. */
  set(key: number, value: VoxelData): this {
    // Hot path of every writer (writePlace per visited cell): kept flat and monomorphic.
    if (this.owner === 0) this.assertMutable()
    if ((key >>> 0) !== key || key >= KEY_END) return this
    const chunkId = key >>> 12
    const clip = this.clip
    if (clip !== null) {
      const c = clip[chunkId]
      if (c !== 2 && (c === 0 || !this.inBounds(key))) return this
    }
    const id = this.palette.intern(value)
    const local = M2L[key & 4095]
    const leaf = this.top[chunkId >>> 9]
    const chunk = leaf ? leaf.chunks[chunkId & 511] : null
    const old = chunk === null ? 0 : chunk.data[local]
    if (old === id) return this
    const writable = chunk !== null && chunk.owner === this.owner ? chunk : this.writableChunk(chunkId)
    writable.data[local] = id
    if (old === 0) { writable.count++; this._size++ }
    return this
  }

  /**
   * Sets `key` to a palette id directly (0 clears the cell). Returns whether the cell changed.
   * Ids must come from this grid's palette.
   */
  setId(key: number, id: number): boolean {
    this.assertMutable()
    if (!this.accepts(key)) return false
    return this.writeId(key, id)
  }

  delete(key: number): boolean {
    this.assertMutable()
    const leaf = this.top[key >>> 21]
    if (!leaf) return false
    const chunk = leaf.chunks[(key >>> 12) & 511]
    if (chunk === null) return false
    const local = M2L[key & 4095]
    if (chunk.data[local] === 0) return false
    const writable = chunk.owner === this.owner ? chunk : this.writableChunk(key >>> 12)
    writable.data[local] = 0
    writable.count--
    this._size--
    return true
  }

  clear(): this {
    this.assertMutable()
    this.top = new Array<ChunkLeaf | null>(TOP_ENTRIES).fill(null)
    this._size = 0
    return this
  }

  /**
   * Applies `op` to the row of cells (x0..x1, y, z), clipped to bounds. Returns the number of cells
   * that changed. Chunks along the span are copied only when a cell in them actually changes.
   */
  writeSpanX(y: number, z: number, x0: number, x1: number, op: SpanOp): number {
    this.assertMutable()
    const b = this.bounds
    if (y < 0 || z < 0 || y >= b.y || z >= b.z || !Number.isInteger(y) || !Number.isInteger(z)) return 0
    if (x0 < 0) x0 = 0
    if (x1 > b.x - 1) x1 = b.x - 1
    if (x0 > x1) return 0
    const kind = op.kind
    const id = kind === 'subtract' || kind === 'subtractIf' ? 0 : op.id
    // accrete: ids the brush may overwrite; subtractIf / repaintIf: ids in scope. Ids beyond the
    // mask's length (interned after it was built) are out of scope.
    const mask = kind === 'accrete' ? op.canOverwrite : (kind === 'subtractIf' || kind === 'repaintIf') ? op.canWrite : null
    const maskLen = mask ? mask.length : 0
    const rowBase = ((z & CHUNK_MASK) << 8) | ((y & CHUNK_MASK) << 4)
    const cy = y >> CHUNK_SHIFT, cz = z >> CHUNK_SHIFT
    let changed = 0
    for (let cx = x0 >> CHUNK_SHIFT, cxEnd = x1 >> CHUNK_SHIFT; cx <= cxEnd; cx++) {
      const originX = cx << CHUNK_SHIFT
      const lx0 = Math.max(x0, originX) - originX
      const lx1 = Math.min(x1, originX + CHUNK_MASK) - originX
      const chunkId = chunkIdOfChunkXYZ(cx, cy, cz)
      let chunk = this.chunkAt(chunkId)
      // Nothing to subtract or repaint in an empty chunk; fill/place/accrete create it on demand.
      if (chunk === null && (kind === 'subtract' || kind === 'repaint' || kind === 'subtractIf' || kind === 'repaintIf')) continue
      let writable = chunk !== null && chunk.owner === this.owner
      for (let lx = lx0; lx <= lx1; lx++) {
        const li = rowBase | lx
        const old = chunk === null ? 0 : chunk.data[li]
        let next: number
        switch (kind) {
          case 'place': next = id; break
          case 'fill': next = old === 0 ? id : old; break
          case 'repaint': next = old === 0 ? 0 : id; break
          case 'subtract': next = 0; break
          case 'subtractIf': next = (old !== 0 && old < maskLen && mask![old] !== 0) ? 0 : old; break
          case 'repaintIf': next = (old !== 0 && old < maskLen && mask![old] !== 0) ? id : old; break
          default: next = (old === 0 || mask![old] !== 0) ? id : old
        }
        if (next === old) continue
        if (!writable) {
          chunk = this.writableChunk(chunkId)
          writable = true
        }
        chunk!.data[li] = next
        if (old === 0) { chunk!.count++; this._size++ } else if (next === 0) { chunk!.count--; this._size-- }
        changed++
      }
    }
    return changed
  }

  // --- Chunk access ---------------------------------------------------------------------------

  getChunk(chunkId: number): VoxelChunk | null {
    return this.chunkAt(chunkId)
  }

  /** Visits every allocated chunk (including ones whose count dropped to 0). */
  forEachChunk(fn: (chunkId: number, chunk: VoxelChunk) => void): void {
    const top = this.top
    for (let t = 0; t < TOP_ENTRIES; t++) {
      const leaf = top[t]
      if (!leaf) continue
      const chunks = leaf.chunks
      for (let s = 0; s < 512; s++) {
        const chunk = chunks[s]
        if (chunk !== null) fn((t << 9) | s, chunk)
      }
    }
  }

  /** Top-level leaf refs (64 entries). Read-only view for diffing; shared across versions. */
  get leaves(): readonly (ChunkLeaf | null)[] {
    return this.top
  }

  /**
   * Replaces the chunk ref at `chunkId` on a transient (null clears it). The grid's size is kept in
   * sync from chunk counts. Used by chunk-level operations such as translate-by-whole-chunks.
   */
  setChunk(chunkId: number, chunk: VoxelChunk | null): void {
    this.assertMutable()
    const leaf = this.writableLeaf(chunkId >>> 9)
    const slot = chunkId & 511
    const prev = leaf.chunks[slot]
    if (prev === chunk) return
    this._size += (chunk ? chunk.count : 0) - (prev ? prev.count : 0)
    leaf.chunks[slot] = chunk
  }

  // --- Internals ------------------------------------------------------------------------------

  private assertMutable(): void {
    if (this.owner === 0) throw new Error('ChunkedVoxelGrid: write to a frozen grid; call asMutable() first')
  }

  /** Key is an integer inside the 27-bit domain and inside this grid's bounds. */
  private accepts(key: number): boolean {
    if ((key >>> 0) !== key || key >= KEY_END) return false
    const clip = this.clip
    if (clip === null) return true
    const c = clip[key >>> 12]
    if (c === 2) return true
    if (c === 0) return false
    return this.inBounds(key)
  }

  /** Per-cell bounds test for keys in partially-inside chunks. */
  private inBounds(key: number): boolean {
    const b = this.bounds
    return parseKeyX(key) < b.x && parseKeyY(key) < b.y && parseKeyZ(key) < b.z
  }

  private chunkAt(chunkId: number): VoxelChunk | null {
    const leaf = this.top[chunkId >>> 9]
    return leaf ? leaf.chunks[chunkId & 511] : null
  }

  /** Writes a validated key; returns whether the cell changed. */
  private writeId(key: number, id: number): boolean {
    const chunkId = key >>> 12
    const local = M2L[key & 4095]
    const existing = this.chunkAt(chunkId)
    const old = existing === null ? 0 : existing.data[local]
    if (old === id) return false
    const chunk = existing !== null && existing.owner === this.owner ? existing : this.writableChunk(chunkId)
    chunk.data[local] = id
    if (old === 0) { chunk.count++; this._size++ } else if (id === 0) { chunk.count--; this._size-- }
    return true
  }

  private writableLeaf(topIndex: number): ChunkLeaf {
    let leaf = this.top[topIndex]
    if (!leaf) {
      leaf = createLeaf(this.owner)
      this.top[topIndex] = leaf
    } else if (leaf.owner !== this.owner) {
      leaf = copyLeaf(leaf, this.owner)
      this.top[topIndex] = leaf
    }
    return leaf
  }

  private writableChunk(chunkId: number): VoxelChunk {
    const leaf = this.writableLeaf(chunkId >>> 9)
    const slot = chunkId & 511
    let chunk = leaf.chunks[slot]
    if (chunk === null) {
      chunk = createChunk(this.owner)
      leaf.chunks[slot] = chunk
      this._freshChunkBytes += CHUNK_BYTES
    } else if (chunk.owner !== this.owner) {
      chunk = copyChunk(chunk, this.owner)
      leaf.chunks[slot] = chunk
      this._freshChunkBytes += CHUNK_BYTES
    }
    return chunk
  }
}

export function isChunkedVoxelGrid(m: unknown): m is ChunkedVoxelGrid {
  return m instanceof ChunkedVoxelGrid
    || (typeof m === 'object' && m !== null && (m as Record<symbol, unknown>)[GRID_BRAND] === true)
}
